import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { RevisionLicenseConclusionScope } from '../../../../../../../shared/model-typings/revisionlicenseconclusion';
import { Action, ResourceType } from '../../../../../../../typings/permissions';
import { useOnConfirm } from '../../../../../../contexts/DialogContext';
import { window } from '../../../../../../globals';
import useHasPermission from '../../../../../../shared/permissions/useHasPermission';
import Button from '../../../../../generic/Button/Button';
import { BUTTON_TYPE } from '../../../../../generic/Button/button-constants';
import { icons, IconText } from '../../../../../generic/Icon';
import { Menu, MenuItem } from '../../../../../generic/Menu';
import type { ButtonSize, ButtonType } from '../../../../../generic/Button/button-constants';
import type { ConclusionData, LocalConclusionRequestScope } from '../../../../../../../shared/types/licenses';

import useConcludeLicense from './hooks/useConcludeLicense';
import useUnconcludeLicense from './hooks/useUnconcludeLicense';
import type { ChooseParams } from './hooks/useConcludeLicense';

/**
 * Organization-wide conclusions apply to every project and release group, so they need org-wide edit on both.
 */
export const useCanConcludeOrgWide = (): boolean => {
  const hasPermission = useHasPermission();
  return !!window.user?.organization?.id
    && hasPermission({ resourceType: ResourceType.ProjectAny, action: Action.Edit })
    && hasPermission({ resourceType: ResourceType.ReleaseGroupAny, action: Action.Edit });
};

type Props = {
  licenseId: string;
  isConcluded: boolean;
  /** How the package is named in the confirm dialogs, e.g. "absl-py 2.2.2". */
  packageName: string;
  conclusions: ConclusionData;
  /** Where a local conclusion is written. */
  chooseParams: ChooseParams;
  /** The scope this page reads conclusions at. An organization-wide change clears the local rows that outrank it. */
  readScope: LocalConclusionRequestScope;
  localNoun: 'project' | 'release group';
  size: ButtonSize;
  idleType: ButtonType;
  labelClassName?: string;
  onDone?: () => void;
};

/**
 * The Conclude / Unconclude button. Users who can edit every project and release group get a menu to apply the
 * change to this project or across the organization.
 */
export const ConcludeMenuButton: VoidFunctionComponent<Props> = ({
  licenseId,
  isConcluded,
  packageName,
  conclusions,
  chooseParams,
  readScope,
  localNoun,
  size,
  idleType,
  labelClassName,
  onDone,
}) => {
  const canConcludeOrgWide = useCanConcludeOrgWide();
  const organizationId = window.user?.organization?.id;
  const onConfirm = useOnConfirm();
  const { mutateAsync: concludeLicense, isPending: isConcluding } = useConcludeLicense(chooseParams);
  const { mutateAsync: unconcludeLicense, isPending: isUnconcluding } = useUnconcludeLicense(chooseParams);
  const isPending = isConcluding || isUnconcluding;
  const verb = isConcluded ? 'Unconclude' : 'Conclude';

  const run = async (params: ChooseParams) => {
    const mutate = isConcluded ? unconcludeLicense : concludeLicense;
    try {
      await mutate({ chosenLicense: licenseId, chooseParams: params });
    } catch {
      // The mutation's error toast already reports the failure, and there is nothing to refresh.
      return;
    }
    onDone?.();
  };

  const onLocal = () => {
    if (!conclusions.scoped.hasOrganizationConclusion) {
      void run(chooseParams);
      return;
    }
    onConfirm({
      title: `${verb} in this ${localNoun} only?`,
      body: `${packageName} has an organization-wide conclusion. This change only `
        + `applies to this ${localNoun}. Everywhere else keeps the organization's conclusion.`,
      confirmText: `${verb} in this ${localNoun}`,
      onConfirm: () => {
        void run(chooseParams);
      },
    });
  };

  const onOrganization = () => {
    if (!organizationId) {
      return;
    }
    const licenses = isConcluded
      ? _.without(conclusions.scoped.licenses, licenseId)
      : _.union(conclusions.scoped.licenses, [licenseId]);
    const outcome = licenses.length > 0
      ? `will be concluded as ${licenses.join(' AND ')}`
      : 'will have no concluded licenses';
    // A local row that only restates the base conclusion (such as the Legal Agent's) is not an override worth naming.
    const hasLocalOverride =
      !!conclusions.scoped.scope
      && conclusions.scoped.scope !== RevisionLicenseConclusionScope.ORGANIZATION
      && !_.isEqual(conclusions.scoped.licenses, conclusions.base.licenses);

    const hasOrganizationConclusion = !!conclusions.scoped.hasOrganizationConclusion;
    const where = hasLocalOverride
      ? `in this ${localNoun} and everywhere else in your organization`
      : 'everywhere in your organization';
    const replaced = hasLocalOverride
      ? ` This ${localNoun}'s own conclusion will be removed`
        + (hasOrganizationConclusion ? ', and your organization\'s current conclusion replaced.' : '.')
      : hasOrganizationConclusion ? ' Your organization\'s current conclusion will be replaced.' : '';

    onConfirm({
      title: `${verb} across your organization?`,
      body: `${packageName} ${outcome} ${where} that doesn't set its own conclusion, `
        + `including future projects and release groups.${replaced}`,
      confirmText: `${verb} across organization`,
      onConfirm: () => {
        void run({
          ...chooseParams,
          scope: { scope: RevisionLicenseConclusionScope.ORGANIZATION, organizationId },
          replaceScope: readScope,
        });
      },
    });
  };

  const renderButton = (onClick: () => void) => (
    <Button
      size={size}
      type={isConcluded ? BUTTON_TYPE.ERROR : idleType}
      isDisabled={isPending}
      onClick={onClick}
    >
      <span className={labelClassName}>
        {canConcludeOrgWide
          ? <IconText icon={icons.chevronDown} text={isPending ? 'Loading...' : verb} alignIcon="right" />
          : isPending ? 'Loading...' : verb}
      </span>
    </Button>
  );

  if (!canConcludeOrgWide) {
    return renderButton(onLocal);
  }

  // react-menu adds its own click handler to the button to open the menu.
  return (
    <Menu menuButton={renderButton(() => undefined)} direction="bottom" align="end" gap={4}>
      <MenuItem onClick={onLocal}>{verb} in this {localNoun}</MenuItem>
      <MenuItem onClick={onOrganization}>{verb} across organization</MenuItem>
    </Menu>
  );
};
